import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import type { RovoLogo } from '@atlassian/rovo-connectors-config/icons';
import type { ProductKeys3P as ProductKeys3PType } from '@atlassian/rovo-connectors-config/types';
import { ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/utils';

import type { OutboundAuthAccountsPayload, SmartLinkEnv } from '../../utils/outboundAuth';
import {
	buildSmartLinkAuthUrl,
	detectEnvFromPayload,
	fetchOutboundAuthAccounts,
	hasSmartLinkContainerMapping,
	isAuthenticatedForProduct,
} from '../../utils/outboundAuth';
import {
	resolveRecommendedProductKeys,
	type SmartlinkConnectorRecommendationInputs,
} from '../../utils/smartlink-connector-recommendations';

const DEFAULT_MAX_CONNECTORS = 3;

export interface ConnectorInfo {
	authUrl: string;
	displayName: string;
	icon: RovoLogo | undefined;
	isAuthenticated: boolean;
	productKey: string;
	requiresOauth: boolean;
}

export interface Use3pConnectorResourcesResult {
	connectorResources: ConnectorInfo[];
	isLoading: boolean;
	onConnectorConnected: (productKey: string) => void;
}

// Build the connector list from the static recommendation cascade + canonical
// container map. Connection state comes from outbound-auth ground truth.
const buildConnectors = async (
	maxConnectors: number,
	outboundAuthPayload: OutboundAuthAccountsPayload | null,
	recommendedProductKeys: ReadonlyArray<ProductKeys3PType>,
	env: SmartLinkEnv | undefined,
): Promise<ConnectorInfo[]> => {
	const mappedProductKeys = recommendedProductKeys
		// Drop providers without a canonical container mapping in this env —
		// rendering a Connect button for them would either 404 or open the
		// wrong OAuth flow.
		.filter((productKey) => hasSmartLinkContainerMapping(productKey, env))
		.slice(0, maxConnectors);

	const resolved: Array<ConnectorInfo | null> = await Promise.all(
		mappedProductKeys.map(async (productKey) => {
			const staticConfig = await ThirdPartyConfigs.get(productKey);
			const authUrl = buildSmartLinkAuthUrl(productKey, env);
			if (!authUrl) {
				return null;
			}
			return {
				productKey,
				displayName: String(staticConfig?.displayName || productKey),
				authUrl,
				icon: staticConfig?.icon,
				requiresOauth: staticConfig?.requiresOauth ?? true,
				isAuthenticated: isAuthenticatedForProduct(outboundAuthPayload, productKey, env),
			};
		}),
	);

	return resolved.filter((entry): entry is ConnectorInfo => entry !== null);
};

/**
 * Returns up to `maxConnectors` Smart Link connectors for account setup.
 *
 * Connector list comes from the recommendation cascade + canonical container
 * map. Connection state comes from outbound-auth ground truth (`aaId`
 * required — without it `isAuthenticated` is always `false`).
 *
 * This intentionally no longer reads the CDS smartlinks-connector resources:
 * CDS is edition-gated and lazy-init, so it silently underreports the
 * connectable universe on free-tier and brand-new tenants.
 */
export const use3pConnectorResources = ({
	cloudId,
	userId,
	aaId,
	maxConnectors = DEFAULT_MAX_CONNECTORS,
	smartlinkConnectorRecommendationInputs,
	productKeys,
}: {
	aaId?: string;
	cloudId: string;
	maxConnectors?: number;
	/**
	 * Optional explicit list of product keys. When provided, skips the
	 * internal recommendation cascade and resolves these keys verbatim
	 * (still subject to `hasSmartLinkContainerMapping` and `maxConnectors`).
	 * Takes precedence over `smartlinkConnectorRecommendationInputs`.
	 */
	productKeys?: ReadonlyArray<ProductKeys3PType>;
	smartlinkConnectorRecommendationInputs?: SmartlinkConnectorRecommendationInputs;
	userId?: string;
}): Use3pConnectorResourcesResult => {
	const [connectorResources, setConnectorResources] = useState<ConnectorInfo[]>([]);
	const [isLoading, setIsLoading] = useState(true);
	const [outboundAuthPayload, setOutboundAuthPayload] =
		useState<OutboundAuthAccountsPayload | null>(null);
	// Smart Link environment detected from the outbound-auth response. Stored
	// in state so the reconcile useEffect can pass it through to the helpers
	// without having to re-detect on every render.
	const [smartLinkEnv, setSmartLinkEnv] = useState<SmartLinkEnv | undefined>(undefined);
	const siteWideSmartlinkAppCounts =
		smartlinkConnectorRecommendationInputs?.siteWideSmartlinkAppCounts;
	const teamTypeSmartlinkAppCounts =
		smartlinkConnectorRecommendationInputs?.teamTypeSmartlinkAppCounts;
	// Stabilise the override by content so callers can pass an inline
	// `.map(...)` without thrashing downstream deps, while still detecting
	// in-place mutation (same reference, different content).
	const productKeysRef = useRef<ReadonlyArray<ProductKeys3PType> | undefined>(undefined);
	const stableProductKeys = useMemo(() => {
		if (!productKeys) {
			productKeysRef.current = undefined;
			return undefined;
		}
		const prev = productKeysRef.current;
		if (
			prev &&
			prev.length === productKeys.length &&
			prev.every((k, i) => k === productKeys[i])
		) {
			return prev;
		}
		productKeysRef.current = productKeys;
		return productKeys;
	}, [productKeys]);
	// Caller-provided override wins; otherwise fall back to the 3-tier
	// cascade so cold-start tenants still get a sensible default. Both
	// branches respect `maxConnectors` so downstream consumers can rely on
	// the cap regardless of which path produced the list.
	const recommendedProductKeys = useMemo(
		() =>
			stableProductKeys
				? stableProductKeys.slice(0, maxConnectors)
				: resolveRecommendedProductKeys(
						{
							siteWideSmartlinkAppCounts,
							teamTypeSmartlinkAppCounts,
							teamTypeKey: smartlinkConnectorRecommendationInputs?.teamTypeKey,
						},
						maxConnectors,
					),
		[
			stableProductKeys,
			siteWideSmartlinkAppCounts,
			teamTypeSmartlinkAppCounts,
			smartlinkConnectorRecommendationInputs?.teamTypeKey,
			maxConnectors,
		],
	);

	useEffect(() => {
		setIsLoading(true);

		(async () => {
			let fetchedOutboundAuth: OutboundAuthAccountsPayload | null = null;
			try {
				// `fetchAccountsByAaId` caches by aaId for 30 minutes, so
				// co-mounted callers (e.g. post-office banner + onboarding
				// modal) share a single network request.
				fetchedOutboundAuth = await fetchOutboundAuthAccounts(aaId);

				// Detect env from the payload UUIDs (env-scoped). Falls back
				// to hostname inside the helpers when the payload is empty.
				const detectedEnv = detectEnvFromPayload(fetchedOutboundAuth) ?? undefined;

				setOutboundAuthPayload(fetchedOutboundAuth);
				setSmartLinkEnv(detectedEnv);

				const connectors = await buildConnectors(
					maxConnectors,
					fetchedOutboundAuth,
					recommendedProductKeys,
					detectedEnv,
				);
				setConnectorResources(connectors);
			} catch {
				// Defensive — `fetchOutboundAuthAccounts` is non-throwing,
				// but keep the safety net so a render-time error still
				// degrades gracefully instead of stalling the spinner.
				setOutboundAuthPayload(fetchedOutboundAuth);
				const fallbackEnv = detectEnvFromPayload(fetchedOutboundAuth) ?? undefined;
				const connectors = await buildConnectors(
					maxConnectors,
					fetchedOutboundAuth,
					recommendedProductKeys,
					fallbackEnv,
				);
				setConnectorResources(connectors);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [aaId, maxConnectors, recommendedProductKeys]);

	const onConnectorConnected = useCallback(
		(productKey: string) => {
			// Keep our `isAuthenticated` in sync so sibling consumers of
			// this hook (e.g. the post-office banner) reflect the new state.
			setConnectorResources((currentResources) =>
				currentResources.map((resource) =>
					resource.productKey === productKey ? { ...resource, isAuthenticated: true } : resource,
				),
			);

			// Bypass the 30-min cache so a later re-mount sees the fresh
			// grant instead of the stale pre-OAuth payload.
			if (aaId) {
				void fetchOutboundAuthAccounts(aaId, { forceRefresh: true }).then((payload) => {
					if (payload) {
						setOutboundAuthPayload(payload);
					}
				});
			}
		},
		[aaId],
	);

	// Reconcile each row's `isAuthenticated` against outbound-auth ground
	// truth whenever the payload changes. The shallow diff prevents an
	// infinite render loop when nothing actually changed.
	useEffect(() => {
		if (!outboundAuthPayload) {
			return;
		}
		setConnectorResources((currentResources) => {
			let didChange = false;
			const next = currentResources.map((resource) => {
				if (!hasSmartLinkContainerMapping(resource.productKey, smartLinkEnv)) {
					return resource;
				}
				const groundTruth = isAuthenticatedForProduct(
					outboundAuthPayload,
					resource.productKey,
					smartLinkEnv,
				);
				if (groundTruth === resource.isAuthenticated) {
					return resource;
				}
				didChange = true;
				return { ...resource, isAuthenticated: groundTruth };
			});
			return didChange ? next : currentResources;
		});
	}, [outboundAuthPayload, smartLinkEnv]);

	return { connectorResources, isLoading, onConnectorConnected };
};
